import type {
  BulkDeleteResult,
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateDto,
  UpdateDto,
} from 'switch-shared/modules/crud/base/base.service';
import {
  AggregateSensorDataInput,
  AggregateSensorDataOutput,
} from 'switch-shared/modules/crud/sensor-data/sensor-data.dto';
import {
  LinkSensorDto,
  SearchSensorsInput,
  SearchSensorsOutput,
} from 'switch-shared/modules/crud/sensor/sensor.dto';
import { Sensor } from 'switch-shared/modules/crud/sensor/sensor.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const sensorAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.SENSOR, ENDPOINT_URLS.SENSOR_DATA],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      createSensor: builder.mutation<Sensor, CreateDto<Sensor>>({
        query: (body) => {
          return {
            method: API_METHODS.POST,
            url: `${ENDPOINT_URLS.SENSOR}`,
            body,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.SENSOR],
      }),

      updateSensor: builder.mutation<
        Sensor,
        { id: string; body: UpdateDto<Sensor> }
      >({
        query: ({ id, body }) => {
          return {
            method: API_METHODS.PATCH,
            url: `${ENDPOINT_URLS.SENSOR}/${id}`,
            body,
          };
        },
        invalidatesTags: [ENDPOINT_URLS.SENSOR],
      }),

      bulkRenameSensors: builder.mutation<BulkRenameResult[], BulkRenameItem[]>(
        {
          query: (items) => ({
            url: `${ENDPOINT_URLS.SENSOR}/bulk-rename`,
            method: API_METHODS.PATCH,
            body: { items },
          }),
          invalidatesTags: [ENDPOINT_URLS.SENSOR],
        }
      ),

      bulkDeleteSensor: builder.mutation<BulkDeleteResult[], string[]>({
        query: (ids) => ({
          method: API_METHODS.DELETE,
          url: `${ENDPOINT_URLS.SENSOR}`,
          body: ids,
        }),
        invalidatesTags: [ENDPOINT_URLS.SENSOR],
      }),

      bulkTransferSensor: builder.mutation<
        Sensor[],
        { ids: string[]; applicationId: string }
      >({
        query: ({ ids, applicationId }) => ({
          method: API_METHODS.PATCH,
          url: `${ENDPOINT_URLS.SENSOR}/bulk-transfer`,
          body: { ids, applicationId },
        }),
        invalidatesTags: [ENDPOINT_URLS.SENSOR],
      }),

      linkByEid: builder.mutation<Sensor, LinkSensorDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.SENSOR}/link`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.SENSOR],
      }),

      searchSensors: builder.query<SearchSensorsOutput, SearchSensorsInput>({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: ENDPOINT_URLS.SENSOR,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.SENSOR],
      }),

      fetchSensor: builder.query<Sensor, { id: string }>({
        query: ({ id }) => {
          return {
            method: API_METHODS.GET,
            url: `${ENDPOINT_URLS.SENSOR}/${id}`,
          };
        },
        providesTags: [ENDPOINT_URLS.SENSOR],
      }),

      aggregateSensorData: builder.query<
        AggregateSensorDataOutput,
        AggregateSensorDataInput
      >({
        query: (params) => {
          return {
            method: API_METHODS.GET,
            url: `${ENDPOINT_URLS.SENSOR_DATA}/aggregateSensorData`,
            params,
          };
        },
        providesTags: [ENDPOINT_URLS.SENSOR_DATA],
      }),
    }),
  });

export const {
  useCreateSensorMutation,
  useUpdateSensorMutation,
  useBulkRenameSensorsMutation,
  useBulkDeleteSensorMutation,
  useBulkTransferSensorMutation,
  useLinkByEidMutation,
  useSearchSensorsQuery,
  useFetchSensorQuery,
  useAggregateSensorDataQuery,
} = sensorAPI;
export default sensorAPI;

setToast('bulkDeleteSensor', {
  bulkResults: { noun: 'sensor', verb: 'delete' },
});
setToast('bulkRenameSensors', {
  bulkResults: { noun: 'sensor', verb: 'rename' },
});
